import type { PlaneKey } from './joints'
import type { HumanoidModel, PointKey } from './model'
import { pointFor, sideDistance } from './model'
import type { Side } from './gait'
import { SIDES } from './gait'
import {
  add,
  apply,
  eulerZXY,
  fractionOf,
  identity,
  lerpVec,
  multiply,
  transpose,
  vec,
  type Mat3,
  type Vec3,
} from './vec'

/**
 * Vorwärtskinematik — analytisch, ohne Szene.
 *
 * DAS IST DER SCHRITT, DER DIE RECHNUNG VOM BILDSCHIRM LÖST.
 *
 * Die Vorlage rechnet die Punktbahnen nicht aus, sie liest sie ab: sie stellt
 * die Three.js-Szene in jede Phase (`applyPose`), lässt `updateMatrixWorld()`
 * die Kette durchrechnen und zieht die Punkte aus `matrixWorld`. Das ist
 * bequem und funktioniert — solange ein Renderer läuft. Es heisst aber auch,
 * dass die Momente eines Antriebs davon abhängen, dass jemand ein Bild
 * gezeichnet hat.
 *
 * Hier steht dieselbe Kette als Rechnung. Die Versätze kommen aus denselben
 * Gelenkpunkten, die auch das Bild bestimmen; die festen Zwischenmasse
 * (Beckenhöhe, Halslänge und so fort) stehen im Skelett und sind an einer
 * Stelle nachlesbar statt über den Szenenaufbau verteilt.
 *
 * ZWEI KOORDINATENSYSTEME, EINE UMRECHNUNG
 *
 *   Gelenkpunkte (Konstruktion):  x vorn, y zur LINKEN Seite, z nach OBEN
 *   Kinematik (hier):             x vorn, y nach OBEN,        z zur RECHTEN Seite
 *
 * Die Umrechnung geschieht ausschliesslich in `buildSkeleton`. Wer sie an
 * mehreren Stellen macht, vertauscht früher oder später zwei Achsen — und ein
 * seitenverkehrter Roboter geht immer noch, nur eben spiegelbildlich.
 */

/** Drehreihenfolge aller Gelenke: erst um y, dann um x, dann um z. */
const DEG = Math.PI / 180

/* -------------------------------------------------------------------------
   Das Skelett
   ------------------------------------------------------------------------- */

/**
 * Die festen Zwischenmasse des Aufbaus, im lokalen Massstab.
 *
 * Sie stehen in der Vorlage als Zahlenliterale im Szenenaufbau verstreut. Hier
 * sind sie beisammen und Teil des Modells — wer den Rumpf länger macht, ändert
 * eine Zahl und nicht drei Stellen im Code.
 *
 * Alle Werte werden mit dem Aufbaumassstab multipliziert; die aus den
 * Gelenkpunkten abgeleiteten Längen NICHT (dort kürzt er sich weg, siehe
 * `gait.ts`).
 */
export interface Frame {
  /** Höhe des Beckenknotens über dem Boden in der Grundstellung. */
  pelvisRest: number
  /** Hüftknoten unterhalb des Beckenknotens. */
  hipDrop: number
  /**
   * Wie weit der Brustkorb über die Schulterachse hinausreicht.
   *
   * Das ist eine Bauteilgrösse, keine Gelenklage: der Halsknoten sitzt oben auf
   * der Brustschale, und die endet ein Stück über der Schulterachse.
   */
  chestOvershoot: number
  /** Halbmesser des Kopfes — der Kopfknoten sitzt um so viel über dem Halsende. */
  headRadius: number
  /** Ferse hinter dem Sohlenpunkt. WELTMASS, wird nicht skaliert. */
  heelBehindSole: number
  /** Zehenspitze vor dem Zehengrundgelenk. */
  toeAhead: number
  /** Zehenspitze unter dem Zehengrundgelenk. */
  toeDrop: number
  /** Untergrenzen der abgeleiteten Rumpfstrecken — verhindern entartete Ketten. */
  minSpineRise: number
  minChestRise: number
  minShoulderRise: number
  minHeadOffset: number
  minNeckLength: number
}

export const HS2_FRAME: Frame = {
  pelvisRest: 0.95,
  hipDrop: -0.02,
  chestOvershoot: 0.055,
  headRadius: 0.082,
  heelBehindSole: 0.1,
  toeAhead: 0.086,
  toeDrop: -0.017,
  minSpineRise: 0.05,
  minChestRise: 0.01,
  minShoulderRise: 0.04,
  minHeadOffset: 0.1,
  minNeckLength: 0.03,
}

/** Alle Längen der Kette in WELTMETERN, fertig gerechnet. */
/**
 * Was an einer Seite hängt.
 *
 * DIESE MASSE KÖNNEN LINKS UND RECHTS VERSCHIEDEN SEIN, alle anderen im
 * Skelett nicht: Rumpf, Hals und Kopf gibt es einmal. Ohne gewollte Unsymmetrie
 * sind beide Sätze Zahl für Zahl gleich — der Normalfall kostet nichts als eine
 * zweite Referenz auf dieselben Werte.
 */
export interface Limbs {
  /** Halber Beinachsabstand, als Betrag. Das Vorzeichen setzt die Seite. */
  hipLateral: number
  thigh: number
  shank: number
  /** Halber Schulterabstand, als Betrag. */
  shoulderLateral: number
  upperArm: number
  forearm: number
  /** Zehengrundgelenk im Fusssystem. */
  mtp: Vec3
  /** Ferse im Fusssystem. */
  heel: Vec3
  /** Ballen im Fusssystem. */
  ball: Vec3
  /** Zehenspitze im Zehensystem. */
  toe: Vec3
}

export interface Skeleton {
  pelvisRest: number
  hipDrop: number
  /**
   * Die Masse je Seite.
   *
   * Wer eine Länge braucht und die Seite kennt, nimmt sie hier. Die flachen
   * Felder darunter sind die RECHTE Seite und stehen für alles, was ohnehin nur
   * eine Zahl kennt — Bauteilradien, Zeichnungsmassstäbe, Kennzahlen.
   */
  limb: Record<Side, Limbs>
  /** Halber Beinachsabstand; rechts positiv. */
  hipLateral: number
  thigh: number
  shank: number
  /** Lendenknoten (Kreuzgelenk) über dem Becken. */
  spineRise: number
  /** Brustknoten über dem Lendenknoten. */
  chestRise: number
  /** Halsknoten über dem Brustknoten — Oberkante der Brustschale. */
  neckRise: number
  /** Kopfmitte über dem Halsknoten. */
  headRise: number
  /** Schulterknoten über dem Brustknoten. */
  shoulderRise: number
  /** Freie Halslänge zwischen Brustschale und Kopfkugel. */
  neckLength: number
  /** Halber Schulterabstand; rechts positiv. */
  shoulderLateral: number
  upperArm: number
  forearm: number
  /** Zehengrundgelenk im Fusssystem. */
  mtp: Vec3
  /** Ferse im Fusssystem. */
  heel: Vec3
  /** Ballen im Fusssystem. */
  ball: Vec3
  /** Zehenspitze im Zehensystem. */
  toe: Vec3
}

/**
 * Baut die Kette aus den Gelenkpunkten.
 *
 * Hier — und nur hier — findet die Umrechnung zwischen dem
 * Konstruktionssystem der Punkte und dem Kinematiksystem statt.
 */
export function buildSkeleton(model: HumanoidModel, frame: Frame = HS2_FRAME): Skeleton {
  const s = model.scale
  const p = model.points
  const mm = (v: number) => v / 1000

  const limb: Record<Side, Limbs> = { R: sideLimbs(model, frame, 'R'), L: sideLimbs(model, frame, 'L') }

  /*
    DER RUMPF FOLGT DEN PUNKTEN.

    Diese fünf Strecken standen anfangs als feste Zahlen hier — übernommen aus
    dem Szenenaufbau der Vorlage. Das war falsch: die Vorlage setzt dort nur
    Startwerte, die sie beim ersten Durchlauf aus dem Punktesatz überschreibt.
    Mit den Startwerten sitzt die Schulter 111 mm und die Kopfmitte 184 mm zu
    tief, und — schwerer wiegend — wer den Brustflansch oder die Kopfmitte im
    Punktesatz verschiebt, bekommt keinerlei Wirkung. Genau das soll dieser
    Rechenkern können.

    Nullpunkt des Punktesatzes ist das Kreuzgelenk des Rumpfaktuators; z zählt
    nach oben. Der Beckenknoten liegt um den Betrag des Hüftversatzes über der
    Hüftnickachse, deshalb geht er von der ersten Strecke ab.
  */
  const spineRise = Math.max(frame.minSpineRise * s, mm(p.cross.z - p.hipN.z) + frame.hipDrop * s)
  const chestRise = Math.max(frame.minChestRise * s, mm(p.chest.z - p.cross.z))
  const shoulderRise = Math.max(frame.minShoulderRise * s, mm(p.shoulder.z - p.chest.z))
  const headOffset = Math.max(frame.minHeadOffset * s, mm(p.headC.z - p.chest.z))
  /* Oberkante der Brustschale — dort sitzt der Halsknoten. */
  const chestTop = shoulderRise + frame.chestOvershoot * s
  const neckLength = Math.max(frame.minNeckLength * s, headOffset - chestTop - frame.headRadius * s)

  return {
    pelvisRest: frame.pelvisRest * s,
    hipDrop: frame.hipDrop * s,
    limb,
    spineRise,
    chestRise,
    neckRise: chestTop,
    headRise: neckLength + frame.headRadius * s,
    shoulderRise,
    neckLength,
    /* Die flachen Felder sind die RECHTE Seite — siehe `Skeleton.limb`. */
    ...limb.R,
  }
}

/**
 * Die Masse EINER Seite.
 *
 * Ohne gewollte Unsymmetrie liefert der Aufruf für beide Seiten dieselben
 * Zahlen; `pointFor` gibt dann schlicht den gemeinsamen Punkt zurück.
 */
function sideLimbs(model: HumanoidModel, frame: Frame, side: Side): Limbs {
  const s = model.scale
  const mm = (v: number) => v / 1000
  const at = (key: PointKey) => pointFor(model, key, side)

  const ankle = at('ankle')
  const sole = at('sole')
  const mtpPoint = at('mtp')

  /* Fusshöhe: Sprunggelenkmitte über der Sohle. */
  const footHeight = mm(ankle.z - sole.z)
  /* Zehengrundgelenk relativ zur Sprunggelenkmitte. */
  const mtpAhead = mm(mtpPoint.x - ankle.x)
  const mtpDrop = mm(mtpPoint.z - ankle.z)

  /*
    Die Zehenspitze wird mit dem Fuss mitskaliert: wird der Fuss länger, wandert
    sie mit. In der Vorlage über einen Faktor gegen den Ausgangszustand — hier
    über das Verhältnis zum Referenzfuss, das dieselbe Wirkung hat und ohne
    zusätzlichen Zustand auskommt.
  */
  const refAhead = mm(HS2_REFERENCE.mtpAhead)
  const refHeight = mm(HS2_REFERENCE.footHeight)
  const stretch = refAhead > 0 ? mtpAhead / refAhead : 1
  const squash = refHeight > 0 ? footHeight / refHeight : 1

  return {
    hipLateral: mm(at('hipN').y),
    thigh: sideDistance(model, 'hipN', 'knee', side),
    shank: sideDistance(model, 'knee', 'ankle', side),
    shoulderLateral: mm(at('shoulder').y),
    upperArm: sideDistance(model, 'shoulder', 'elbow', side),
    forearm: sideDistance(model, 'elbow', 'wrist', side),
    mtp: vec(mtpAhead, mtpDrop, 0),
    /* Der Fersenversatz ist ein Weltmass: die Vorlage teilt ihn durch den
       Massstab, bevor die Szene ihn wieder damit multipliziert. */
    heel: vec(mm(sole.x) - frame.heelBehindSole, -footHeight, 0),
    ball: vec(mtpAhead, -footHeight, 0),
    toe: vec(frame.toeAhead * s * stretch, frame.toeDrop * s * squash, 0),
  }
}

/** Referenzfuss des HS-2, in Millimetern — Bezug für die Zehenskalierung. */
const HS2_REFERENCE = { mtpAhead: 105, footHeight: 70 }

/* -------------------------------------------------------------------------
   Die Pose
   ------------------------------------------------------------------------- */

/** Winkel eines Gelenks in einer Phase, in Grad. */
export type AngleLookup = (joint: string, plane: PlaneKey, side: Side) => number

/**
 * DIE GRUNDSTELLUNG — die Winkel, in denen die Maschine steht, wenn sie nichts tut.
 *
 * Sie ist NICHT „alle Achsen auf null", und das ist ein Befund und keine
 * Feinheit. Mit durchgestrecktem Ellbogen hängt die Hand im Becken: der Abstand
 * zwischen Handkapsel und Beckenkapsel ist dann −63 mm, die beiden durchdringen
 * einander. Mit 22° Beugung sind es +33 mm.
 *
 * Wer die Nullstellung für die Ruhelage hält, bekommt deshalb eine
 * Freigangsprüfung, die überall anschlägt, einen Arbeitsraum, der leer ist, und
 * Achsgrenzen, die alle null sind — nicht weil die Maschine steif wäre, sondern
 * weil sie in einer Stellung geprüft wurde, die sie nie einnimmt.
 *
 * Der Wert steht hier und nicht in der Ganggenerierung, obwohl er dort zuerst
 * gebraucht wurde: eine Ruhelage ist eine Eigenschaft der Maschine und nicht
 * eine des Gehens.
 */
export const REST_ANGLES: Record<string, Partial<Record<PlaneKey, number>>> = {
  /* Der Mittelwert der Schwungkurve, gerundet. Der Arm hängt leicht gebeugt. */
  elbow: { sag: 22 },
}

/** Die Grundstellung als Nachschlagefunktion — für Prüfungen ohne Bewegung. */
export const restAngle: AngleLookup = (joint, plane) => REST_ANGLES[joint]?.[plane] ?? 0

export interface Transform {
  position: Vec3
  rotation: Mat3
}

const compose = (parent: Transform, offset: Vec3, local: Mat3): Transform => ({
  position: add(parent.position, apply(parent.rotation, offset)),
  rotation: multiply(parent.rotation, local),
})

/** Alle Knoten einer Pose, in Weltkoordinaten. */
export interface Pose {
  pelvis: Transform
  spine: Transform
  chest: Transform
  neck: Transform
  head: Transform
  hip: Record<Side, Transform>
  knee: Record<Side, Transform>
  ankle: Record<Side, Transform>
  mtp: Record<Side, Transform>
  shoulder: Record<Side, Transform>
  elbow: Record<Side, Transform>
  wrist: Record<Side, Transform>
}

export interface PelvisPlacement {
  /** Waagerechter Versatz des Beckens, m. */
  x: number
  /** Höhe des Beckenknotens, m. */
  y: number
  /** Seitlicher Versatz, m. */
  z: number
}

/**
 * Stellt die Kette in eine Pose.
 *
 * `angles` liefert den Winkel eines Gelenks in Grad. Die Vorzeichen sind aus
 * der Vorlage übernommen und nicht selbstverständlich:
 *
 *  · Die Beckenkippung geht mit umgekehrtem Vorzeichen ein (`rotation.z = −tilt`),
 *    weil die aufgezeichnete Kippung nach VORN positiv zählt, die Drehung um die
 *    Querachse aber nach hinten.
 *  · Frontale und transversale Winkel werden auf der linken Seite gespiegelt
 *    (`× s`), weil die Kurven „nach innen" und „nach aussen" beschreiben, nicht
 *    „nach links" und „nach rechts".
 *  · Der Kniewinkel geht negativ ein: die Kurve zählt Beugung positiv, die
 *    Drehung um die Querachse streckt.
 *
 * Wer eines dieser Vorzeichen dreht, bekommt eine Figur, die sich bewegt und
 * dabei falsch ist — das ist der unangenehmste Fehler dieser Kette.
 */
export function poseAt(
  skeleton: Skeleton,
  angles: AngleLookup,
  pelvis: PelvisPlacement,
  options: { pelvisRestOffset?: number; headFollow?: number } = {},
): Pose {
  const restOffset = options.pelvisRestOffset ?? -3
  const headFollow = options.headFollow ?? 1

  const tilt = (angles('pelvis', 'sag', 'R') - restOffset) * DEG
  const pelvisNode: Transform = {
    position: vec(pelvis.x, pelvis.y, pelvis.z),
    rotation: eulerZXY(
      angles('pelvis', 'fro', 'R') * DEG,
      angles('pelvis', 'tra', 'R') * DEG,
      -tilt,
    ),
  }

  const spine = compose(
    pelvisNode,
    vec(0, skeleton.spineRise, 0),
    eulerZXY(
      -angles('lumbar', 'fro', 'R') * DEG,
      angles('lumbar', 'tra', 'R') * DEG,
      tilt + angles('lumbar', 'sag', 'R') * DEG,
    ),
  )

  const chest = compose(
    spine,
    vec(0, skeleton.chestRise, 0),
    eulerZXY(
      -angles('thorax', 'fro', 'R') * DEG,
      angles('thorax', 'tra', 'R') * DEG,
      angles('thorax', 'sag', 'R') * DEG,
    ),
  )

  /*
    BLICKSTABILISIERUNG. Bei `headFollow = 1` sitzt der Kopf starr am Brustkorb
    und dreht sich mit ihm; das ist die einfache Annahme und die falsche. Ein
    Zweibeiner hält den Kopf ruhiger als den Rumpf — nicht aus Höflichkeit,
    sondern weil Kamera und Lagesensor sonst die volle Rumpfschwingung sehen.
    Bei `headFollow = 0,35` folgt der Kopf der Rumpfdrehung nur zu diesem Teil.

    Der Anteil wird als BRUCHTEIL EINER DREHUNG gebildet und nicht als Bruchteil
    dreier Winkel: Euler-Winkel addieren sich nicht, und ein Drittel dreier
    Winkel ist keine Drittel-Drehung. Der Hals bekommt dann genau die Drehung,
    die die Rumpfdrehung auf diesen Anteil zurücknimmt.
  */
  const neckLocal =
    headFollow >= 1
      ? identity()
      : multiply(transpose(chest.rotation), fractionOf(chest.rotation, headFollow))
  const neck = compose(chest, vec(0, skeleton.neckRise, 0), neckLocal)
  const head = compose(neck, vec(0, skeleton.headRise, 0), identity())

  const hip = {} as Record<Side, Transform>
  const knee = {} as Record<Side, Transform>
  const ankle = {} as Record<Side, Transform>
  const mtp = {} as Record<Side, Transform>
  const shoulder = {} as Record<Side, Transform>
  const elbow = {} as Record<Side, Transform>
  const wrist = {} as Record<Side, Transform>

  for (const side of SIDES) {
    const s = side === 'R' ? 1 : -1
    /* Die Masse dieser Seite. Ohne gewollte Unsymmetrie sind sie auf beiden
       Seiten dieselben; mit ihr geht die Figur ungleich, und genau das soll man
       sehen. */
    const L = skeleton.limb[side]

    hip[side] = compose(
      pelvisNode,
      vec(0, skeleton.hipDrop, s * L.hipLateral),
      eulerZXY(
        angles('hip', 'fro', side) * DEG * s,
        angles('hip', 'tra', side) * DEG * s,
        angles('hip', 'sag', side) * DEG,
      ),
    )

    knee[side] = compose(
      hip[side],
      vec(0, -L.thigh, 0),
      eulerZXY(
        angles('knee', 'fro', side) * DEG * s,
        angles('knee', 'tra', side) * DEG * s,
        -angles('knee', 'sag', side) * DEG,
      ),
    )

    ankle[side] = compose(
      knee[side],
      vec(0, -L.shank, 0),
      eulerZXY(
        angles('ankle', 'fro', side) * DEG * s,
        angles('ankle', 'tra', side) * DEG * s,
        angles('ankle', 'sag', side) * DEG,
      ),
    )

    mtp[side] = compose(ankle[side], L.mtp, eulerZXY(0, 0, angles('mtp', 'sag', side) * DEG))

    shoulder[side] = compose(
      chest,
      vec(0, skeleton.shoulderRise, s * L.shoulderLateral),
      eulerZXY(
        angles('shoulder', 'fro', side) * DEG * s,
        angles('shoulder', 'tra', side) * DEG * s,
        angles('shoulder', 'sag', side) * DEG,
      ),
    )

    elbow[side] = compose(
      shoulder[side],
      vec(0, -L.upperArm, 0),
      eulerZXY(0, 0, angles('elbow', 'sag', side) * DEG),
    )

    wrist[side] = compose(elbow[side], vec(0, -L.forearm, 0), identity())
  }

  return {
    pelvis: pelvisNode,
    spine,
    chest,
    neck,
    head,
    hip,
    knee,
    ankle,
    mtp,
    shoulder,
    elbow,
    wrist,
  }
}

/* -------------------------------------------------------------------------
   Punkte, die aus der Pose folgen
   ------------------------------------------------------------------------- */

/** Ein Punkt im Fusssystem in Weltkoordinaten. */
export const footPoint = (ankle: Transform, local: Vec3): Vec3 =>
  add(ankle.position, apply(ankle.rotation, local))

export interface ContactPoints {
  heel: Vec3
  ball: Vec3
  toe: Vec3
}

/** Die drei Bodenpunkte eines Fusses in Weltkoordinaten. */
export function contactPoints(skeleton: Skeleton, pose: Pose, side: Side): ContactPoints {
  const L = skeleton.limb[side]
  return {
    heel: footPoint(pose.ankle[side], L.heel),
    ball: footPoint(pose.ankle[side], L.ball),
    toe: footPoint(pose.mtp[side], L.toe),
  }
}

/**
 * Tiefster Bodenpunkt eines Fusses.
 *
 * Die Grösse, an der sich das Kontaktmodell entscheidet — und die Prüfgrösse
 * „Bodenfehler": in der Standphase muss sie null sein, sonst schwebt der Fuss
 * oder steckt im Boden.
 */
export const soleHeight = (skeleton: Skeleton, pose: Pose, side: Side): number => {
  const c = contactPoints(skeleton, pose, side)
  return Math.min(c.heel.y, c.ball.y, c.toe.y)
}

/**
 * Schwerpunkt eines Segments zwischen zwei Gelenkpunkten.
 *
 * `com` ist der Anteil der Segmentlänge, vom proximalen Gelenk aus.
 */
export const segmentCentre = (proximal: Vec3, distal: Vec3, com: number): Vec3 =>
  lerpVec(proximal, distal, com)

/**
 * Rumpfschwerpunkt: Mitte zwischen Hüftachse und Schulterachse.
 *
 * Aus der Vorlage übernommen — dort als Mittel der beiden Schulterpunkte, dann
 * zur Hälfte zum Becken gezogen.
 */
export function trunkCentre(pose: Pose): Vec3 {
  const shoulders = lerpVec(pose.shoulder.R.position, pose.shoulder.L.position, 0.5)
  return lerpVec(shoulders, pose.pelvis.position, 0.5)
}

/* -------------------------------------------------------------------------
   Benannte Punkte und Bezugssysteme
   ------------------------------------------------------------------------- */

/**
 * Die Punkte, die der Kern über den Zyklus verfolgt — als NAMEN, nicht als
 * Funktionen.
 *
 * Namen und keine Verschlüsse, weil die Bahnen gefiltert werden müssen, bevor
 * irgendetwas abgeleitet wird. Eine Tabelle benannter Bahnen lässt sich in einem
 * Durchgang glätten; über die Pose verstreute Zugriffe nicht — und wer eine
 * davon vergisst, leitet eine ungefilterte Bahn zweimal ab und bekommt einen
 * Ausschlag, der jedes Moment darunter dominiert.
 */
export type NodeKey =
  | 'hip'
  | 'knee'
  | 'ankle'
  | 'mtp'
  | 'ball'
  | 'heel'
  | 'toe'
  | 'shoulder'
  | 'elbow'
  | 'wrist'
  | 'pelvis'
  | 'trunk'
  | 'head'
  | 'hands'

/** Ein benannter Punkt in Weltkoordinaten. Seitenlose Punkte ignorieren `side`. */
export function nodePoint(skeleton: Skeleton, pose: Pose, key: NodeKey, side: Side = 'R'): Vec3 {
  switch (key) {
    case 'hip':
      return pose.hip[side].position
    case 'knee':
      return pose.knee[side].position
    case 'ankle':
      return pose.ankle[side].position
    case 'mtp':
      return pose.mtp[side].position
    case 'ball':
      return footPoint(pose.ankle[side], skeleton.ball)
    case 'heel':
      return footPoint(pose.ankle[side], skeleton.heel)
    case 'toe':
      return footPoint(pose.mtp[side], skeleton.toe)
    case 'shoulder':
      return pose.shoulder[side].position
    case 'elbow':
      return pose.elbow[side].position
    case 'wrist':
      return pose.wrist[side].position
    case 'pelvis':
      return pose.pelvis.position
    case 'trunk':
      return trunkCentre(pose)
    case 'head':
      return pose.head.position
    case 'hands':
      /* Beidhändig getragene Last: die Mitte zwischen den Handgelenken. Es
         braucht keine eigene Zahl dafür — der Sitz folgt aus der Haltung. */
      return lerpVec(pose.wrist.R.position, pose.wrist.L.position, 0.5)
  }
}

/** Bezugssysteme, in denen Trägheiten und Momente gemessen werden. */
export type FrameKey = 'hip' | 'knee' | 'ankle' | 'mtp' | 'shoulder' | 'elbow' | 'chest' | 'pelvis'

export function nodeFrame(pose: Pose, key: FrameKey, side: Side = 'R'): Mat3 {
  switch (key) {
    case 'hip':
      return pose.hip[side].rotation
    case 'knee':
      return pose.knee[side].rotation
    case 'ankle':
      return pose.ankle[side].rotation
    case 'mtp':
      return pose.mtp[side].rotation
    case 'shoulder':
      return pose.shoulder[side].rotation
    case 'elbow':
      return pose.elbow[side].rotation
    case 'chest':
      return pose.chest.rotation
    case 'pelvis':
      return pose.pelvis.rotation
  }
}
